import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analysis as analysisApi, pollJob } from '@/lib/api';
import { useProfileState } from '@/contexts/ProfileContext';
import { SDLogo } from '@/components/atoms';
import { trackEvent, EVENTS } from '@/lib/analytics';

// -----------------------------------------------------------------------------
// AnalysisLoading — user waits ~4min for their real analysis; meanwhile we
// preview the four dashboard tabs against a fictional demo profile so they
// see what's coming AND understand the interactions (flip cards, click badges,
// pick skills, tab through role tracks, expand a contact + find on LinkedIn).
// Every mock uses the SAME visual DNA as the real pages, driven by CSS-only
// keyframe animations for perf + accessibility (respects prefers-reduced-motion).
// -----------------------------------------------------------------------------

const TOTAL_ETA_SEC = 240;

const RAIL_STEPS = [
  { label: 'Reading your CV', min_s: 0 },
  { label: 'Detecting your domain', min_s: 15 },
  { label: 'Mapping skills', min_s: 30 },
  { label: 'Benchmarking roles', min_s: 70 },
  { label: 'Building action plan', min_s: 120 },
  { label: 'Verifying links', min_s: 195 },
];

const PREVIEW_TABS = [
  { id: 'dashboard', label: 'Dashboard',      caption: 'Your career position — one look, everything you need.' },
  { id: 'skills',    label: 'Skills Profile', caption: 'Six axes of your skill stack — with evidence, market position, and the specific level-up.' },
  { id: 'roles',     label: 'Role Explorer',  caption: 'Six role paths across Expected, Adjacent, and Stretch — salary bands + why each fits.' },
  { id: 'network',   label: 'Network In',     caption: 'Archetype contacts with three outreach templates each — and a one-click LinkedIn search.' },
];

/**
 * MOBILE preview content — simple, vertical, no absolute positioning.
 * Mirrors the essence of each landscape mock without trying to squeeze the
 * complex CSS-animated preview into portrait aspect (which was overlapping
 * badly on iPhone-width viewports).
 *
 * Each entry provides:
 *   - `title`   — the headline the user will see in the finished dashboard
 *   - `bullets` — 3-4 short items summarising what the finished tab contains
 *   - `chip`    — a colored pill giving a "flavor" preview of the value
 */
const PREVIEW_TABS_MOBILE = {
  dashboard: {
    title: 'Meridian Group CSRD delivery meets London\'s Director-track hiring wave.',
    body: 'A single hero card, market demand pill, target salary band, and your next best move — all calibrated to your profile.',
    bullets: [
      { label: 'YOUR CAREER POSITION', value: 'Head of Sustainability · Director-track' },
      { label: 'MARKET DEMAND',        value: 'HIGH · Director of Sustainability' },
      { label: 'TARGET SALARY',        value: '£130K–£175K · Greater London' },
      { label: 'YOUR NEXT MOVE',       value: 'Head → Director within 18 months' },
    ],
  },
  skills: {
    title: 'Six axes · your skill stack with real evidence.',
    body: 'A radar of your six sustainability skills, with the specific level-up move for each.',
    bullets: [
      { label: 'TECHNICAL',      value: '8.5 · STRONG' },
      { label: 'STRATEGIC',      value: '8.0 · STRONG' },
      { label: 'DOMAIN',         value: '8.0 · STRONG' },
      { label: 'DATA ANALYSIS',  value: '7.5 · STRONG' },
    ],
  },
  roles: {
    title: 'Three role paths · Expected · Adjacent · Stretch.',
    body: 'Director of Sustainability, an advisory adjacent, and one honest stretch — with salary bands and why each fits.',
    bullets: [
      { label: 'EXPECTED', value: 'Director of Sustainability · 81% match' },
      { label: 'ADJACENT', value: 'Principal, Sustainability Advisory · 72%' },
      { label: 'STRETCH',  value: 'VP / Head of Group Sustainability · 58%' },
      { label: 'BENCHMARK',value: '£95K–£115K → £130K–£175K → £160K–£210K' },
    ],
  },
  network: {
    title: 'Peer Directors + outreach templates.',
    body: 'The specific people, events and communities that will move your pivot from Head to Director — with tone-differentiated outreach templates.',
    bullets: [
      { label: 'PEOPLE',        value: 'Director of Sustainability · listed consumer peers' },
      { label: 'EVENTS',        value: 'IEMA · A4S Summit · Edie Leaders Forum' },
      { label: 'COMMUNITIES',   value: 'CSRD Practitioners · A4S alumni · WBCSD' },
      { label: 'OUTREACH',      value: '3 templates · Peer · Value-first · Warm intro' },
    ],
  },
};

/**
 * MOBILE preview mock components — each one renders a MINIATURE of what the
 * real mobile app view looks like when analysis completes. Uses the same
 * design tokens (green primary, ivory bg, roundies, typography) so the
 * user sees an authentic in-app preview, not a summary card.
 */
function MobileMockDashboard() {
  return (
    <div className="sd-mob-mock sd-mob-mock--dashboard">
      <div className="sd-mob-hero">
        <span className="sd-mob-hero__pill">
          <span className="sd-mob-hero__pill-dot" />YOUR CAREER POSITION
        </span>
        <h4 className="sd-mob-hero__title">
          You run the reporting a Fortune 500 team runs. <mark>On a mid-cap band.</mark>
        </h4>
        <p className="sd-mob-hero__body">
          Six years owning CSRD readiness and Scope 3 across Meridian Group puts your technical depth ahead of most Heads at your level. The blocker is scope — one commercial or transformation remit closes the distance to Director within 18 months.
        </p>
      </div>
      <div className="sd-mob-badges">
        <div className="sd-mob-badge sd-mob-badge--green">
          <div className="sd-mob-badge__val">HIGH</div>
          <div className="sd-mob-badge__lbl">MARKET DEMAND</div>
        </div>
        <div className="sd-mob-badge">
          <div className="sd-mob-badge__val">£130K–£175K</div>
          <div className="sd-mob-badge__lbl">TARGET SALARY</div>
        </div>
      </div>
      <div className="sd-mob-next">
        <span className="sd-mob-next__tag">
          <span className="sd-mob-next__tag-dot" />YOUR NEXT MOVE
        </span>
        <div className="sd-mob-next__flip">
          <div className="sd-mob-next__flip-face">
            <span className="sd-mob-next__flip-pill">STRONGEST MATCH · 81%</span>
            <div className="sd-mob-next__flip-title">Director of Sustainability</div>
            <div className="sd-mob-next__flip-meta">£130K–£175K · Listed Mid-cap Consumer</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileMockSkills() {
  const skills = [
    { label: 'Technical',     score: '8.5', tier: 'STRONG', active: true },
    { label: 'Strategic',     score: '8.0', tier: 'STRONG' },
    { label: 'Domain',        score: '8.0', tier: 'STRONG' },
    { label: 'Data Analysis', score: '7.5', tier: 'STRONG' },
    { label: 'Influence',     score: '7.0', tier: 'DEVELOPING' },
    { label: 'Communication', score: '7.5', tier: 'STRONG' },
  ];
  return (
    <div className="sd-mob-mock sd-mob-mock--skills">
      <div className="sd-mob-skill-radar">
        {/* Simplified hex radar — three concentric hexagons + polygon fill */}
        <svg viewBox="0 0 200 180" className="sd-mob-skill-radar__svg" aria-hidden>
          {[70, 50, 30].map((r, i) => (
            <polygon
              key={i}
              points={mobHexPoints(100, 90, r)}
              fill="none"
              stroke="rgba(76,217,100,0.14)"
              strokeWidth="1"
            />
          ))}
          <polygon
            points={mobHexPoints(100, 90, 60)}
            fill="rgba(76,217,100,0.28)"
            stroke="#4CD964"
            strokeWidth="1.5"
          />
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const a = (Math.PI / 3) * i - Math.PI / 2;
            const x = 100 + 70 * Math.cos(a);
            const y = 90 + 70 * Math.sin(a);
            return <circle key={i} cx={x} cy={y} r={i === 0 ? 4 : 2.5} fill={i === 0 ? '#4CD964' : 'rgba(255,255,255,0.4)'} />;
          })}
        </svg>
        <div className="sd-mob-skill-radar__score">
          <div className="sd-mob-skill-radar__score-num">7.8</div>
          <div className="sd-mob-skill-radar__score-lbl">STRONG</div>
        </div>
      </div>
      <div className="sd-mob-skill-list">
        {skills.map((s, i) => (
          <div key={i} className={`sd-mob-skill-row ${s.active ? 'is-active' : ''}`}>
            <div className="sd-mob-skill-row__label">{s.label}</div>
            <div className="sd-mob-skill-row__right">
              <div className="sd-mob-skill-row__bar"><span style={{ width: `${(parseFloat(s.score) / 10) * 100}%` }} /></div>
              <div className="sd-mob-skill-row__score">{s.score}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileMockRoles() {
  const roles = [
    { badge: 'EXPECTED',  color: '#4CD964', title: 'Director of Sustainability',                sector: 'Listed Mid-cap Consumer',    match: 81 },
    { badge: 'ADJACENT',  color: '#8FA5F0', title: 'Principal, Sustainability Advisory',        sector: 'Consultancy · Advisory',     match: 72 },
    { badge: 'STRETCH',   color: '#F5B94A', title: 'VP / Head of Group Sustainability',         sector: 'Fortune 500 · Listed',       match: 58 },
    { badge: 'EXPECTED',  color: '#4CD964', title: 'Head of ESG · Consumer & FMCG',             sector: 'Listed Mid-cap Consumer',    match: 79 },
  ];
  return (
    <div className="sd-mob-mock sd-mob-mock--roles">
      <div className="sd-mob-salary-banner">
        <div className="sd-mob-salary-banner__side">
          <div className="sd-mob-salary-banner__lbl">SALARY BENCHMARK</div>
          <div className="sd-mob-salary-banner__sub">Director of Sustainability · London</div>
        </div>
        <div className="sd-mob-salary-banner__val">£130K–£175K</div>
      </div>
      <div className="sd-mob-track-tabs">
        {['Expected', 'Adjacent', 'Stretch', 'All'].map((t, i) => (
          <span key={t} className={`sd-mob-track-tab ${i === 0 ? 'is-active' : ''}`}>{t}</span>
        ))}
      </div>
      <div className="sd-mob-role-list">
        {roles.map((r, i) => (
          <div key={i} className="sd-mob-role-card">
            <div className="sd-mob-role-card__top">
              <span className="sd-mob-role-card__badge" style={{ background: `${r.color}22`, color: r.color }}>{r.badge}</span>
              <span className="sd-mob-role-card__match">{r.match}% <span>match</span></span>
            </div>
            <div className="sd-mob-role-card__title">{r.title}</div>
            <div className="sd-mob-role-card__sector">{r.sector}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileMockNetwork() {
  const people = [
    { init: 'D', name: 'Director of Sustainability', role: 'Peer listed consumer group', note: 'Made the exact Head → Director move · warm reference' },
    { init: 'P', name: 'Principal, Sustainability Advisory', role: 'UK Advisory / Big-Four', note: 'A4S alumni · route into P&L-owning practice areas' },
  ];
  const events = ['A4S Summit London', 'Edie Sustainability Leaders Forum', 'IEMA Annual Conference'];
  return (
    <div className="sd-mob-mock sd-mob-mock--network">
      <div className="sd-mob-net-section">
        <div className="sd-mob-net-section__label">
          <span className="sd-mob-net-section__label-dot" />PEOPLE TO MEET
        </div>
        <div className="sd-mob-net-people">
          {people.map((p, i) => (
            <div key={i} className="sd-mob-net-person">
              <div className="sd-mob-net-person__avatar">{p.init}</div>
              <div className="sd-mob-net-person__body">
                <div className="sd-mob-net-person__name">{p.name}</div>
                <div className="sd-mob-net-person__role">{p.role}</div>
                <div className="sd-mob-net-person__note">{p.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="sd-mob-net-section">
        <div className="sd-mob-net-section__label">
          <span className="sd-mob-net-section__label-dot" />EVENTS · PINNED
        </div>
        <div className="sd-mob-net-events">
          {events.map((e, i) => (
            <div key={i} className={`sd-mob-net-event ${i === 0 ? 'is-pinned' : ''}`}>
              <span className="sd-mob-net-event__dot" />
              <span className="sd-mob-net-event__name">{e}</span>
              {i === 0 && <span className="sd-mob-net-event__pin">PINNED</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function mobHexPoints(cx, cy, r) {
  const pts = [];
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
  }
  return pts.join(' ');
}

const MOBILE_MOCKS = {
  dashboard: MobileMockDashboard,
  skills:    MobileMockSkills,
  roles:     MobileMockRoles,
  network:   MobileMockNetwork,
};

const TAB_ROTATION_MS = 9500;

const DEMO = { name: 'Doyan Decoder' };

export default function AnalysisLoading() {
  const navigate = useNavigate();
  const { reload } = useProfileState();
  const [activeStep, setActiveStep] = useState(0);
  const [tabIdx, setTabIdx] = useState(0);
  const [userSelectedTab, setUserSelectedTab] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [phaseTwoHit, setPhaseTwoHit] = useState(false);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const startRef = useRef(Date.now());
  const ranRef = useRef(false);

  useEffect(() => {
    if (userSelectedTab) return;
    const iv = setInterval(() => setTabIdx((i) => (i + 1) % PREVIEW_TABS.length), TAB_ROTATION_MS);
    return () => clearInterval(iv);
  }, [userSelectedTab]);

  useEffect(() => {
    const iv = setInterval(() => setElapsed((Date.now() - startRef.current) / 1000), 1000);
    // When the mobile user returns to the tab, iOS may have thrown away the
    // last few seconds of ticks. Force one immediate recompute so the progress
    // bar and remaining-time snap back to the true elapsed time.
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setElapsed((Date.now() - startRef.current) / 1000);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [retryKey]);

  useEffect(() => {
    const iv = setInterval(() => {
      const e = (Date.now() - startRef.current) / 1000;
      let cur = 0;
      for (let i = 0; i < RAIL_STEPS.length; i += 1) if (e >= RAIL_STEPS[i].min_s) cur = i;
      setActiveStep((s) => Math.max(s, cur));
    }, 1500);
    return () => clearInterval(iv);
  }, [retryKey]);

  const { pct, remainingLabel } = useMemo(() => {
    let p = Math.min(99, (elapsed / TOTAL_ETA_SEC) * 100);
    if (phaseTwoHit) p = Math.max(p, 82);
    p = Math.max(4, p);
    const rounded = Math.round(p);
    const remaining = Math.max(0, Math.round(TOTAL_ETA_SEC - elapsed));
    const label = remaining <= 8 ? 'Wrapping up…'
      : (() => {
          const m = Math.floor(remaining / 60);
          const s = remaining % 60;
          return m > 0 ? `~${m}m ${s.toString().padStart(2, '0')}s remaining` : `~${s}s remaining`;
        })();
    return { pct: rounded, remainingLabel: label };
  }, [elapsed, phaseTwoHit]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    // Resume-or-start: if a mobile tab was backgrounded and iOS killed the page,
    // the user returns to a fresh mount but the analysis is already running on
    // the backend. Check localStorage for a live job and RESUME polling it
    // instead of firing a duplicate `runAsync`. We use a 15-min freshness
    // window — anything older is treated as stale and we start fresh.
    const STORAGE_KEY = 'sd_active_analysis_job';
    let resumed = null;
    try {
      const rawStored = localStorage.getItem(STORAGE_KEY);
      if (rawStored) {
        const stored = JSON.parse(rawStored);
        if (stored?.job_id && stored?.started_at) {
          const ageMs = Date.now() - stored.started_at;
          if (ageMs < 15 * 60 * 1000) {
            resumed = stored;
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }
    } catch { /* corrupt storage — ignore, start fresh */ }

    startRef.current = resumed ? resumed.started_at : Date.now();
    setElapsed(resumed ? (Date.now() - resumed.started_at) / 1000 : 0);
    setPhaseTwoHit(false);

    (async () => {
      try {
        let job_id;
        if (resumed?.job_id) {
          job_id = resumed.job_id;
        } else {
          const resp = await analysisApi.runAsync();
          job_id = resp.job_id;
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ job_id, started_at: startRef.current }));
          } catch { /* private mode / quota — non-fatal */ }
        }
        await pollJob(job_id, {
          intervalMs: 4000, timeoutMs: 600000,
          onProgress: (j) => {
            const r = j.result || {};
            if (r.phase === 2) { setPhaseTwoHit(true); setActiveStep((s) => Math.max(s, 4)); }
          },
        });
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        setActiveStep(RAIL_STEPS.length - 1);
        setPhaseTwoHit(true);
        // Instrumentation: analysis (or re-run) completed. Fire fire-and-forget.
        try {
          const isRerun = typeof window !== 'undefined' && window.sessionStorage?.getItem('sd_last_analysis_completed_ts');
          trackEvent(isRerun ? EVENTS.assessment_rerun : EVENTS.analysis_completed, {});
          try { window.sessionStorage?.setItem('sd_last_analysis_completed_ts', String(Date.now())); } catch { /* no-op */ }
        } catch (_e) { /* no-op */ }
        await reload();
        setTimeout(() => navigate('/dashboard', { replace: true }), 900);
      } catch (e) {
        // Only clear the stored job if the backend actually reports it errored
        // or the safety timeout was reached; transient network fails inside
        // pollJob are already tolerated there.
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        setError(e?.response?.data?.detail || e?.message || 'Analysis failed.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  if (error) {
    return (
      <div className="min-h-screen bg-sd-black text-white flex flex-col">
        <div className="px-8 py-5"><SDLogo color="#FFFFFF" accent="#4CD964" /></div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-[520px] w-full text-center">
            <div className="text-[#C94040] text-[40px] mb-3">!</div>
            <h2 className="text-[24px] font-semibold mb-3">Analysis failed</h2>
            <p className="text-[#A8B0A8] text-[14px] mb-7">{error}</p>
            <div className="flex gap-3 justify-center">
              <button className="sd-btn-secondary" onClick={() => navigate('/onboarding/review', { replace: true })} data-testid="analysis-back">Back to review</button>
              <button className="sd-btn-primary" onClick={() => { setError(null); ranRef.current = false; setRetryKey((k) => k + 1); }} data-testid="analysis-retry">Retry analysis</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentTab = PREVIEW_TABS[tabIdx];

  return (
    <div className="sd-loading-shell" data-testid="analysis-loading-shell">
      <header className="sd-loading-header">
        <div className="sd-loading-header__brand">
          <SDLogo color="#FFFFFF" accent="#4CD964" />
          <span className="sd-loading-header__tag">Preparing your calibrated view</span>
        </div>
        <div className="sd-loading-status" data-testid="analysis-status">
          <div className="sd-loading-status__row">
            <div className="sd-loading-status__pct" data-testid="analysis-pct">{pct}%<span>complete</span></div>
            <div className="sd-loading-status__meta">
              <div className="sd-loading-status__eta" data-testid="analysis-eta-total">Total: ~4 min</div>
              <div className="sd-loading-status__remaining" data-testid="analysis-remaining">{remainingLabel}</div>
            </div>
          </div>
          <div className="sd-loading-status__bar" aria-hidden="true">
            <div className="sd-loading-status__fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </header>

      <div className="sd-loading-body">
        {/* MOBILE-only vertical preview. Shown at ≤900px in place of the
            complex landscape mock which does not fit portrait viewports.
            Mirrors the essence of each tab with clean typography + bullets. */}
        <div className="sd-loading-preview-mobile" role="region" aria-label="Demo dashboard preview">
          <div className="sd-loading-preview__tabs sd-loading-preview__tabs--mobile" role="tablist" aria-label="Dashboard tabs">
            {PREVIEW_TABS.map((t, i) => (
              <button key={t.id} type="button" role="tab" aria-selected={i === tabIdx}
                className={`sd-loading-preview__tab ${i === tabIdx ? 'is-active' : ''}`}
                onClick={() => { setTabIdx(i); setUserSelectedTab(true); }}
                data-testid={`preview-tab-mobile-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="sd-loading-mobile-card" key={currentTab.id} data-testid={`preview-mobile-card-${currentTab.id}`}>
            <div className="sd-loading-mobile-card__url">
              <span className="sd-loading-mobile-card__url-dot" />
              <span className="sd-loading-mobile-card__url-text">https://sustainabilitydecoded.com/{currentTab.id}</span>
            </div>
            <div className="sd-loading-mobile-card__body">
              {(() => {
                const MockComp = MOBILE_MOCKS[currentTab.id];
                return MockComp ? <MockComp /> : null;
              })()}
            </div>
          </div>

          <p className="sd-loading-legend" data-testid="analysis-demo-banner-mobile">
            <strong>Demo preview · {DEMO.name}</strong> — this is what your dashboard will look like when analysis completes. Your version will contain <em>your</em> data.
          </p>
        </div>

        <div className="sd-loading-preview" role="region" aria-label="Demo dashboard preview">
          <div className="sd-loading-preview__tabs" role="tablist" aria-label="Dashboard tabs">
            {PREVIEW_TABS.map((t, i) => (
              <button key={t.id} type="button" role="tab" aria-selected={i === tabIdx}
                className={`sd-loading-preview__tab ${i === tabIdx ? 'is-active' : ''}`}
                onClick={() => { setTabIdx(i); setUserSelectedTab(true); }}
                data-testid={`preview-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
            <span className="sd-loading-preview__tab-hint">Auto-cycling — click to hold</span>
          </div>

          <div className="sd-loading-frame">
            <div className="sd-loading-chrome">
              <span className="sd-loading-chrome__dot sd-loading-chrome__dot--red" />
              <span className="sd-loading-chrome__dot sd-loading-chrome__dot--amber" />
              <span className="sd-loading-chrome__dot sd-loading-chrome__dot--green" />
              <div className="sd-loading-chrome__url">https://sustainabilitydecoded.com/{currentTab.id}</div>
            </div>
            <div className="sd-loading-stage" key={currentTab.id}>
              {currentTab.id === 'dashboard' && <TabDashboard />}
              {currentTab.id === 'skills'    && <TabSkills />}
              {currentTab.id === 'roles'     && <TabRoles />}
              {currentTab.id === 'network'   && <TabNetwork />}
            </div>
          </div>

          <div className="sd-loading-caption" key={tabIdx}>{currentTab.caption}</div>

          <p className="sd-loading-legend" data-testid="analysis-demo-banner">
            <strong>Demo preview · {DEMO.name}</strong> — this is what your dashboard will look like when analysis completes. Your version will contain <em>your</em> data.
          </p>
        </div>

        <aside className="sd-loading-rail sd-loading-rail--vertical" data-testid="progress-rail" aria-label="Analysis progress">
          {RAIL_STEPS.map((step, i) => {
            const state = i < activeStep ? 'done' : i === activeStep ? 'active' : 'pending';
            return (
              <React.Fragment key={step.label}>
                <div className={`sd-rail-node sd-rail-node--${state}`} data-testid={`rail-step-${i}`} data-state={state}>
                  <div className="sd-rail-dot" aria-hidden="true">
                    {state === 'done' ? '✓' : state === 'active' ? <span className="sd-rail-spinner" /> : null}
                  </div>
                  <div className="sd-rail-label">{step.label}</div>
                </div>
                {i < RAIL_STEPS.length - 1 && (
                  <div className={`sd-rail-line sd-rail-line--${i < activeStep ? 'done' : 'pending'}`} />
                )}
              </React.Fragment>
            );
          })}
        </aside>
      </div>
    </div>
  );
}

// =============================================================================
// TAB 1 — DASHBOARD (mirrors the real Overview page)
// =============================================================================
const GRADIENT_GREEN =
  'radial-gradient(120% 80% at 12% 8%, rgba(255,255,255,.22), transparent 55%),' +
  'linear-gradient(142deg, #1EA968 0%, #0F8452 48%, #0A5A3B 100%)';

function TabDashboard() {
  return (
    <div className="sd-mock sd-mock--dashboard">
      <div className="sd-mock-hero" style={{ background: GRADIENT_GREEN }}>
        <div className="sd-mock-hero__pill"><span className="sd-mock-hero__pill-dot" />Your career position</div>
        <h1 className="sd-mock-hero__title">
          You run the reporting a Fortune 500 team runs. <span className="sd-mock-hero__highlight">On a mid-cap band.</span>
        </h1>
        <p className="sd-mock-hero__body">
          Six years owning CSRD readiness and Scope 3 across a £2B group puts your technical depth ahead of most Heads at your level. The blocker is scope — you've led the function, not the P&amp;L behind it — and one commercial or transformation remit closes the distance to Director within 18 months.
        </p>
      </div>

      <div className="sd-mock-badges">
        <div className="sd-mock-badge sd-mock-badge--demand">
          <div className="sd-mock-badge__val">HIGH</div>
          <div className="sd-mock-badge__lbl">Market demand<span className="sd-mock-badge__caret" /></div>
        </div>
        <div className="sd-mock-badge sd-mock-badge--salary">
          <div className="sd-mock-badge__val">£130K–£175K</div>
          <div className="sd-mock-badge__lbl">Target salary<span className="sd-mock-badge__caret" /></div>
        </div>
      </div>

      {/* Reveal panel — defaults to "Your next move" flip card; when Target Salary
          is emulated-clicked, the salary landscape slides in over it. */}
      <div className="sd-mock-reveal">
        <div className="sd-mock-section-label">
          <span className="sd-mock-section-label__dot" />YOUR NEXT MOVE
        </div>

        {/* Flip card */}
        <div className="sd-mock-flip">
          <div className="sd-mock-flip__inner">
            <div className="sd-mock-flip__face sd-mock-flip__face--front">
              <div className="sd-mock-flip__row">
                <div className="sd-mock-pill sd-mock-pill--mint">STRONGEST MATCH</div>
                <div className="sd-mock-flip__cta">Flip this card <span className="sd-mock-flip__icon">↻</span></div>
              </div>
              <div className="sd-mock-flip__body">
                <div className="sd-mock-flip__title">Director of Sustainability</div>
                <p className="sd-mock-flip__desc">Your CSRD and Scope 3 delivery at Meridian Group is already Director-grade; what's missing is org-level budget ownership, not technical range. A one-step move, not a leap.</p>
                <div className="sd-mock-flip__meta">
                  <span className="sd-mock-flip__salary">£130K–£175K</span>
                  <span>Listed Mid-cap Consumer · London</span>
                </div>
              </div>
              <div className="sd-mock-flip__ring">
                <MatchRing pct={81} />
              </div>
            </div>
            <div className="sd-mock-flip__face sd-mock-flip__face--back">
              <div className="sd-mock-flip__row">
                <div className="sd-mock-pill sd-mock-pill--dark">A ROLE YOU HADN'T CONSIDERED</div>
                <div className="sd-mock-flip__cta sd-mock-flip__cta--light">Back to your match <span className="sd-mock-flip__icon">↻</span></div>
              </div>
              <div className="sd-mock-flip__body">
                <div className="sd-mock-flip__title sd-mock-flip__title--light">Principal, Sustainability Advisory</div>
                <p className="sd-mock-flip__desc sd-mock-flip__desc--light">A context shift — same expertise, sold from outside. Not a level change; the trade is stability for range: ten sectors a year instead of one. Fastest route to widening the sector fluency Directors are hired for.</p>
                <div className="sd-mock-flip__meta sd-mock-flip__meta--light">
                  <span className="sd-mock-flip__salary">£110K–£150K</span>
                  <span>UK Advisory · London</span>
                </div>
              </div>
              <div className="sd-mock-flip__ring">
                <MatchRing pct={72} dark />
              </div>
            </div>
          </div>
        </div>

        {/* Salary landscape — slides in when Target Salary badge is emulated-clicked */}
        <div className="sd-mock-salary-overlay">
          <div className="sd-mock-section-label">
            <span className="sd-mock-section-label__dot" />CURRENT · TARGET · STRETCH
          </div>
          <div className="sd-mock-salary-card">
            {[
              { k: 'Current',   v: '£95K–£115K',  w: '54%', hi: false },
              { k: 'Target',    v: '£130K–£175K', w: '83%', hi: true },
              { k: 'Stretch',   v: '£160K–£210K', w: '100%', hi: false },
            ].map((r) => (
              <div key={r.k} className="sd-mock-salary-row">
                <div className={`sd-mock-salary-row__k ${r.hi ? 'is-hi' : ''}`}>{r.k}</div>
                <div className="sd-mock-salary-row__bar"><div style={{ width: r.w, background: r.hi ? '#4CD964' : '#A8D7B0' }} /></div>
                <div className={`sd-mock-salary-row__v ${r.hi ? 'is-hi' : ''}`}>{r.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchRing({ pct, dark }) {
  const dash = Math.round((pct / 100) * 264);
  return (
    <div className="sd-mock-ring">
      <svg width="58" height="58" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke={dark ? 'rgba(255,255,255,.15)' : '#EEF1EF'} strokeWidth="9" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="#4CD964" strokeWidth="9" strokeDasharray={`${dash} 264`} strokeLinecap="round" transform="rotate(-90 50 50)" />
      </svg>
      <div className={`sd-mock-ring__val ${dark ? 'is-light' : ''}`}>{pct}%<span>MATCH</span></div>
    </div>
  );
}

// =============================================================================
// TAB 2 — SKILLS PROFILE (mirrors the real Skills Profile page)
// =============================================================================
const SKILLS_CYCLE = [
  { key: 'technical',  label: 'Technical',     score: '8.5', tier: 'STRONG',
    evidence: 'You built Scope 3 coverage across 40 category-A suppliers at Meridian Group and delivered CSRD readiness aligned to EFRAG guidance.',
    market:   'Above the technical bar for Head of Sustainability shortlists at listed mid-caps.',
    levelup:  'Adding SBTi FLAG or CDP water-security depth extends you into consumer-goods Director tracks.', hi: 'top' },
  { key: 'strategic',  label: 'Strategic',     score: '8.0', tier: 'STRONG',
    evidence: 'You owned the board narrative for Meridian Group\'s first CSRD report and a three-year Scope 3 roadmap tied to the FY27 CapEx plan.',
    market:   'Meets the strategic bar for Head roles; approaching Director-level scope.',
    levelup:  'Owning a commercial or transformation P&L line, not just the function, is the step to Director.', hi: 'right' },
  { key: 'domain',     label: 'Domain',        score: '8.0', tier: 'STRONG',
    evidence: 'Eight years across corporate sustainability spanning reporting, strategy and supplier engagement in consumer and industrial groups.',
    market:   'Meets the domain bar for Head of Sustainability at listed mid-caps.',
    levelup:  'Depth in one adjacent area — climate risk or nature/TNFD — broadens you across more Head and Director briefs.', hi: 'bottom' },
  { key: 'analysis',   label: 'Data Analysis', score: '7.5', tier: 'STRONG',
    evidence: 'You model Scope 3 across the supplier base and ran TCFD scenario analysis for the UK entity.',
    market:   'Above median for the analytical rigour listed-company sustainability committees now filter for.',
    levelup:  'IPCC AR6 pathway or NGFS scenario methodology opens climate-risk lead roles.', hi: 'left' },
];

function TabSkills() {
  return (
    <div className="sd-mock sd-mock--skills">
      <div className="sd-mock-skills__grid">
        <div className="sd-mock-web">
          {/* Auto-cycled floating pill above the active axis */}
          {SKILLS_CYCLE.map((s, i) => (
            <div key={s.key} className={`sd-mock-web__pill sd-mock-web__pill--${s.hi} sd-mock-web-anim-${i}`}>{s.label}</div>
          ))}
          <svg viewBox="0 0 300 300" width="100%" height="100%" className="sd-mock-web__svg">
            {[0.85, 0.65, 0.45, 0.25].map((r, i) => (
              <polygon key={i} points={hexPoints(150, 150, 120 * r)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            ))}
            {axisEndpoints(150, 150, 120).map((p, i) => (
              <line key={i} x1="150" y1="150" x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            ))}
            {/* Doyan's skill polygon — 6 scores: 8.5, 8.0, 7.0, 8.0, 7.5, 7.5 (Technical → Communication) */}
            <polygon points={hexPoints(150, 150, 120, [0.85, 0.80, 0.70, 0.80, 0.75, 0.75])} fill="rgba(76,217,100,0.22)" stroke="#4CD964" strokeWidth="2" />
            {axisEndpoints(150, 150, 120, [0.85, 0.80, 0.70, 0.80, 0.75, 0.75]).map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="4" fill="#4CD964" style={{ filter: 'drop-shadow(0 0 6px rgba(76,217,100,0.7))' }} />
            ))}
          </svg>
          {/* Axis labels */}
          {['Technical', 'Strategic', 'Influence', 'Domain', 'Data Analysis', 'Communication'].map((l, i) => (
            <div key={l} className={`sd-mock-web__lbl sd-mock-web__lbl--${i}`}>{l}</div>
          ))}
          {/* Central score ring */}
          <div className="sd-mock-web__center">
            {SKILLS_CYCLE.map((s, i) => (
              <div key={s.key} className={`sd-mock-web__score sd-mock-web-anim-${i}`}>
                <div className="sd-mock-web__score-val">{s.score}</div>
                <div className="sd-mock-web__score-tier">{s.tier}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="sd-mock-insights">
          {SKILLS_CYCLE.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`sd-mock-insight sd-mock-insight--evidence sd-mock-web-anim-${i}`}>
                <div className="sd-mock-insight__lbl">Your evidence</div>
                <p className="sd-mock-insight__body">{s.evidence}</p>
              </div>
              <div className={`sd-mock-insight sd-mock-web-anim-${i}`}>
                <div className="sd-mock-insight__lbl">Market position</div>
                <p className="sd-mock-insight__body">{s.market}</p>
              </div>
              <div className={`sd-mock-insight sd-mock-insight--peak sd-mock-web-anim-${i}`}>
                <div className="sd-mock-insight__lbl">Maintain at peak</div>
                <p className="sd-mock-insight__body">{s.levelup}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function hexPoints(cx, cy, r, scales) {
  const s = scales || Array(6).fill(1);
  return Array.from({ length: 6 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    return `${cx + Math.cos(a) * r * s[i]},${cy + Math.sin(a) * r * s[i]}`;
  }).join(' ');
}
function axisEndpoints(cx, cy, r, scales) {
  const s = scales || Array(6).fill(1);
  return Array.from({ length: 6 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    return { x: cx + Math.cos(a) * r * s[i], y: cy + Math.sin(a) * r * s[i] };
  });
}

// =============================================================================
// TAB 3 — ROLE EXPLORER (mirrors the real Role Explorer page)
// =============================================================================
const ROLES_BY_TRACK = {
  expected: [
    { sector: 'Listed Mid-cap Consumer', title: 'Director of Sustainability', match: 81,
      body: 'Your CSRD and Scope 3 delivery at Meridian Group is already Director-grade; what\'s missing is org-level budget ownership, not technical range. This is a one-step move, not a leap.' },
    { sector: 'Listed Mid-cap Consumer', title: 'Head of ESG · Consumer & FMCG', match: 79,
      body: 'You already run the reporting cycle these teams need at scale — the move here is lateral within the Head band, buying you sector fluency before the Director step.' },
  ],
  adjacent: [
    { sector: 'Consultancy · Advisory', title: 'Principal, Sustainability Advisory', match: 72,
      body: 'A context shift — same expertise, sold from outside. This is a move into advisory, not a level change. The trade is stability for range: ten sectors a year instead of one.' },
  ],
  stretch: [
    { sector: 'Fortune 500 · Listed', title: 'VP / Head of Group Sustainability', match: 58,
      body: 'The reach isn\'t your technical depth — it\'s operating at group scale across multiple business units and a board reporting line you haven\'t held yet. An 18–24 month build, not a sideways step.' },
  ],
};
const TRACKS = ['expected', 'adjacent', 'stretch'];

function TabRoles() {
  return (
    <div className="sd-mock sd-mock--roles">
      <div className="sd-mock-salary-banner" style={{ background: GRADIENT_GREEN }}>
        <div>
          <div className="sd-mock-salary-banner__lbl">Salary Benchmark · Head of Sustainability · Mid-cap Consumer Group</div>
          <div className="sd-mock-salary-banner__sub">Greater London Area · +10–20% bonus typical at listed mid-caps</div>
        </div>
        <div className="sd-mock-salary-banner__side">
          <div className="sd-mock-salary-banner__val">£95K–£115K</div>
          <div className="sd-mock-salary-banner__sub">BASE RANGE</div>
        </div>
      </div>

      <div className="sd-mock-role-tabs">
        {['Expected', 'Adjacent', 'Stretch', 'All'].map((t, i) => (
          <div key={t} className={`sd-mock-role-tab sd-mock-role-tab-anim-${i}`}>{t}</div>
        ))}
      </div>

      <div className="sd-mock-role-list">
        {TRACKS.map((track, i) => (
          <div key={track} className={`sd-mock-role-list__group sd-mock-role-anim-${i}`}>
            {ROLES_BY_TRACK[track].map((r, j) => (
              <div key={j} className="sd-mock-role-card">
                <div className="sd-mock-role-card__head">
                  <div className="sd-mock-role-card__type">
                    <span className={`sd-mock-role-card__tag sd-mock-role-card__tag--${track}`}>{track.toUpperCase()}</span>
                    <span className="sd-mock-role-card__sector">{r.sector}</span>
                  </div>
                  <div className="sd-mock-role-card__match">{r.match}<span>%</span><div>MATCH</div></div>
                </div>
                <div className="sd-mock-role-card__title">{r.title}</div>
                <p className="sd-mock-role-card__body">{r.body}</p>
                <div className="sd-mock-role-card__foot"><span className="sd-mock-role-card__caret">⌄</span>Show details</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// TAB 4 — NETWORK IN (mirrors the real Network In page)
// =============================================================================
function TabNetwork() {
  return (
    <div className="sd-mock sd-mock--network">
      <div className="sd-mock-net-header">
        <h1 className="sd-mock-net-title">Network In</h1>
        <p className="sd-mock-net-sub">The specific people, events and communities that will move your pivot from Head to Director.</p>
        <div className="sd-mock-net-section">People to meet</div>
      </div>

      <div className="sd-mock-person">
        <div className="sd-mock-person__row">
          <div className="sd-mock-person__avatar">D</div>
          <div className="sd-mock-person__info">
            <div className="sd-mock-person__name">Director of Sustainability at a peer listed consumer group</div>
            <div className="sd-mock-person__role">Director of Sustainability &amp; ESG</div>
            <div className="sd-mock-person__meta">LinkedIn / A4S alumni network · Search LinkedIn for Director of Sustainability at listed mid-cap consumer or industrial groups — your Meridian Group CSRD delivery is a warm operator-to-operator opener.</div>
          </div>
          <div className="sd-mock-person__toggle sd-mock-person-anim__toggle">−</div>
        </div>

        <div className="sd-mock-person__body sd-mock-person-anim__body">
          <div className="sd-mock-person__grid">
            <div className="sd-mock-person__why">
              <div className="sd-mock-person__label">WHY VALUABLE</div>
              <p>They made the exact move you're targeting — functional lead to P&amp;L-owning Director. First-hand insight into how the remit and comp changed, and a warm reference if you move.</p>
            </div>
            <div className="sd-mock-person__outreach">
              <div className="sd-mock-person__tabs">
                <span className="sd-mock-person__tab sd-mock-person-anim__tab-a">Peer-to-peer</span>
                <span className="sd-mock-person__tab sd-mock-person-anim__tab-b">Value-first insight</span>
                <span className="sd-mock-person__copy">Copy</span>
              </div>
              <div className="sd-mock-person__msg">
                <div className="sd-mock-person__msg-a sd-mock-person-anim__msg-a">
                  Hi — we crossed paths around the CSRD reporting cycle last year. I'm mapping the step from Head of Sustainability into a Director remit with real budget ownership, and you've done exactly that. Would you have 20 minutes to share how the scope and the conversations changed? Genuinely just want to learn from someone who's made the move.
                </div>
                <div className="sd-mock-person__msg-b sd-mock-person-anim__msg-b">
                  Hi — I saw your team just closed the FY25 CSRD report cycle. I'm on the delivery side of the same wave at Meridian Group and hit the supplier data-quality wall you wrote about in September. Happy to share how we solved audit-trail continuity for category-A — and I'd love to hear how the Director scope changed the way you approached the same problem.
                </div>
              </div>
            </div>
          </div>
          <button className="sd-mock-person__cta sd-mock-person-anim__cta">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Find on LinkedIn
          </button>
        </div>

        {/* Mock LinkedIn side panel — slides in when "Find on LinkedIn" is emulated-clicked */}
        <div className="sd-mock-li-panel sd-mock-person-anim__li">
          <div className="sd-mock-li-panel__chrome">
            <div className="sd-mock-li-panel__logo">in</div>
            <div className="sd-mock-li-panel__search">Director of Sustainability listed consumer UK</div>
          </div>
          <div className="sd-mock-li-panel__filters"><span>People</span><span>Companies</span><span>Jobs</span></div>
          <div className="sd-mock-li-panel__results">
            {[
              { i: 'PM', n: 'Priya M.',   r: 'Director of Sustainability — Listed Consumer', hop: '2nd', mut: '9 mutual connections' },
              { i: 'JT', n: 'James T.',   r: 'Director, ESG & Climate — UK Industrial',      hop: '3rd', mut: 'A4S alumni network' },
              { i: 'RN', n: 'Rachel N.',  r: 'Head of Sustainability — Listed FMCG',         hop: '2nd', mut: '12 mutual connections' },
            ].map((p, i) => (
              <div key={i} className="sd-mock-li-panel__result">
                <div className="sd-mock-li-panel__avatar">{p.i}</div>
                <div className="sd-mock-li-panel__info">
                  <div className="sd-mock-li-panel__name">{p.n} <span>· {p.hop}</span></div>
                  <div className="sd-mock-li-panel__role">{p.r}</div>
                  <div className="sd-mock-li-panel__mut">{p.mut}</div>
                </div>
                <button className="sd-mock-li-panel__btn">Connect</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
